import { PropertyGroup } from "types/property-group";
import { FC, ReactNode, useMemo } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Settings02 } from "untitledui-js-base";
import { handleCopy } from "utils/handleCopy";
import { Folder01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { formatTitle } from "utils/formatTitle";
import { plural } from "pluralize";
import { getStateLabel } from "utils/stateCodes";
import { PropertyGroupOptionBadgeList } from "shared/components/PropertyGroupOptionBadgeList";

import { propertyGroupTitleMap } from "views/property-groups-configuration/constants";
import { PropertyGroupSplitBy } from "views/property-groups-configuration/types";

type GroupInfoItemProps = {
  label: string;
  value: ReactNode;
};

const GroupInfoItem: FC<GroupInfoItemProps> = ({ label, value }) => {
  return (
    <div className="space-y-2 min-w-[140px]">
      <p className="text-xs">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
};

interface LayoutHeaderProps {
  onSettingsClick: () => void;
  propertyGroup?: PropertyGroup;
  selectedSplitByOptions: PropertyGroupSplitBy[];
}

export const LayoutHeader: FC<LayoutHeaderProps> = ({
  onSettingsClick,
  propertyGroup,
  selectedSplitByOptions
}) => {
  const { Property } = useUnitsPropertiesDictionary();

  const availableOptions = useMemo(
    () =>
      selectedSplitByOptions.map((option, index) => {
        const splitData = propertyGroup?.split_data_chain[index + 1] || {
          is_other: true,
          split_type: "",
          split_values: []
        };

        const formatValue = (val: string) => {
          if (!val) return "(empty)";
          if (option === PropertyGroupSplitBy.STATE) return getStateLabel(val);
          if (option === PropertyGroupSplitBy.CITY) return formatTitle(val);
          return formatTitle(val);
        };

        if (splitData.is_other)
          return {
            label: formatTitle(plural(propertyGroupTitleMap[option])),
            value: [`Other ${plural(propertyGroupTitleMap[option])}`]
          };

        return {
          label: formatTitle(plural(propertyGroupTitleMap[option])),
          value: splitData.split_values.map(formatValue)
        };
      }),
    [propertyGroup, selectedSplitByOptions]
  );

  return (
    <div className="divide-y divide-alpha-40 border-b border-alpha-100">
      <div className="flex justify-between items-center py-3 px-6">
        <BreadCrumbs
          crumbs={[
            { title: `${Property} groups`, href: "/property-groups" },
            {
              title:
                propertyGroup &&
                propertyGroup.name &&
                propertyGroup.name.length > 30
                  ? `${propertyGroup.name.slice(0, 30)}...`
                  : propertyGroup?.name || "",
              isActive: true
            }
          ]}
        />
        <div className="flex gap-2">
          <Button
            borderLess
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            className="stroke-content-400 text-content-400 hover:bg-alpha-40"
            disabled={!propertyGroup?.listing_url}
            onClick={() =>
              handleCopy(propertyGroup?.listing_url || "", "Link copied")
            }
            HugeIcon={Folder01Icon}
          >
            <p className="hidden md:flex">Copy listing page</p>
          </Button>
          <Button
            borderLess
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            className="stroke-content-400 text-content-400 hover:bg-alpha-40"
            onClick={onSettingsClick}
            Icon={Settings02}
          >
            <p className="hidden md:flex">Settings</p>
          </Button>
        </div>
      </div>

      <h1 className="text-2xl font-bold p-6">{propertyGroup?.name || ""}</h1>

      <div className="p-6 flex gap-8">
        <GroupInfoItem
          label="Screening settings"
          value={propertyGroup?.verification_template?.name || "No template"}
        />
        {availableOptions.map((option) => (
          <GroupInfoItem
            key={option.label}
            label={option.label}
            value={<PropertyGroupOptionBadgeList values={option.value} />}
          />
        ))}
      </div>
    </div>
  );
};
